/**
 * Signal Quality Badge — deterministic post-refresh evaluator.
 *
 * Renders the colored badge (green/yellow/red), score 0-10, primary
 * market+side, EV%, Kelly, expandable pros/risks, and a "Send to
 * Personal Telegram" button.
 *
 * Pure presentational: receives the `signal_quality` payload from the
 * `/refresh-now/{match_id}` response and surfaces it. No fetches, no
 * timers — the parent forwards the build form to a modal on click.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Send, Sparkles } from 'lucide-react';

const COLOR_MAP = {
  green: {
    bg: 'bg-emerald-950/40',
    border: 'border-emerald-700',
    text: 'text-emerald-300',
    chip: 'bg-emerald-700/40 text-emerald-200',
    label: '🟢 GREEN',
  },
  yellow: {
    bg: 'bg-amber-950/40',
    border: 'border-amber-700',
    text: 'text-amber-300',
    chip: 'bg-amber-700/40 text-amber-200',
    label: '🟡 YELLOW',
  },
  red: {
    bg: 'bg-rose-950/40',
    border: 'border-rose-700',
    text: 'text-rose-300',
    chip: 'bg-rose-700/40 text-rose-200',
    label: '🔴 RED',
  },
};

const ACTION_LABEL = {
  BET_FULL_FRACTION: 'Bet at full Kelly fraction',
  LIVE_ONLY_OR_SMALL_STAKE: 'Live-only or small stake',
  SKIP_OR_PAPER_ONLY: 'Skip — paper only',
};

const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`);
const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

export default function SignalQualityBadge({ data, matchInfo, onSendPersonal }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;
  const color = COLOR_MAP[data.signal_color] || COLOR_MAP.yellow;
  const candidates = data.candidates_breakdown || [];

  const handleSendClick = (cand) => {
    // Build initial modal form from the candidate.
    onSendPersonal({
      match_id: matchInfo?.id || matchInfo?.match_id,
      market: cand.market,
      side: cand.side,
      line: cand.line,
      odds: cand.odds,
      probability_used: cand.probability_used,
      ev_pct: cand.ev_pct,
      stake_units: data.kelly_suggested_units || 0.5,
      signal_quality: data,
      is_red: data.signal_color === 'red',
    });
  };

  const primarySide = data.side
    ? `${data.signal_type} (${data.side}${data.line ? ` @ ${data.line}` : ''})`
    : data.signal_type;

  return (
    <div
      data-testid="signal-quality-badge"
      className={`rounded-sm border ${color.bg} ${color.border} px-3 py-2 text-[11px] space-y-2`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span
          data-testid="signal-quality-color"
          className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold tracking-wider ${color.chip}`}
        >
          {color.label}
        </span>
        <span data-testid="signal-quality-score" className={`${color.text} text-sm font-bold`}>
          {fmtNum(data.signal_score, 1)}/10
        </span>
        <span className="text-zinc-400">·</span>
        <span data-testid="signal-quality-type" className="text-zinc-100 font-medium">
          {primarySide}
        </span>
        {data.ev_pct != null && (
          <>
            <span className="text-zinc-400">·</span>
            <span
              className="text-zinc-300"
              title="Conservative EV. Uses p_lo80 (80% lower bound) × odds − 1. The O/U / HC tables show raw EV (uses p_calibrated)."
              data-testid="signal-quality-ev-cons"
            >
              EV cons <b className={data.ev_pct > 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {fmtPct(data.ev_pct)}
              </b>
            </span>
            {data.ev_pct_raw != null && (
              <span
                className="text-zinc-500 text-[10px]"
                title="Raw EV. Uses p_calibrated (no shrinkage) × odds − 1. Shown for audit; betting / Kelly use the conservative EV above."
                data-testid="signal-quality-ev-raw"
              >
                (raw <b className={data.ev_pct_raw > 0 ? 'text-emerald-200' : 'text-rose-200'}>
                  {fmtPct(data.ev_pct_raw)}
                </b>)
              </span>
            )}
          </>
        )}
        {data.kelly_suggested_units != null && (
          <>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-300">
              Kelly <b className="text-zinc-100">{fmtNum(data.kelly_suggested_units, 2)}u</b>
              <span className="text-zinc-500"> ({fmtNum(data.kelly_fraction_pct, 0)}%)</span>
            </span>
          </>
        )}
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="signal-quality-toggle"
            className="text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            details
          </button>
          {data.candidates_breakdown && data.candidates_breakdown.length > 0 && (
            <button
              type="button"
              data-testid="send-to-personal-telegram-btn"
              onClick={() => {
                const primary = candidates.find(
                  (c) => c.market === data.primary_market && c.ev_pct != null,
                ) || candidates.find((c) => c.ev_pct != null);
                if (primary) handleSendClick(primary);
              }}
              className="px-2 py-1 rounded-sm bg-violet-700/40 hover:bg-violet-600/50 text-violet-100 text-[10px] flex items-center gap-1"
              title="Send personal Telegram signal (separate stats)"
            >
              <Send size={11} /> Send to Personal
            </button>
          )}
        </span>
      </div>

      <div data-testid="signal-quality-action" className={`text-[10px] ${color.text}`}>
        <Sparkles size={10} className="inline-block mr-1" />
        {ACTION_LABEL[data.final_action] || data.final_action}
      </div>

      {/* 2026-05-08 — Cross-Market Selection Conflict banner.
          Surfaces a high-impact disagreement between the Paper Signal
          (MW) and the SetProfile shadow engine when multi-model
          agreement is low AND SetScore is far from the market AND
          shadow points to a structurally cleaner non-MW market.
          Audit/UI only: Telegram / Kelly / Policy Guard untouched. */}
      {data.cross_market_conflict?.conflict && (
        <div
          data-testid="cross-market-conflict-banner"
          className="rounded-sm px-2 py-1.5 text-[10px] flex items-start gap-2"
          style={{
            background: 'rgba(244,63,94,0.10)',
            border: '1px solid rgba(244,63,94,0.40)',
            color: '#FECACA',
          }}
        >
          <span
            className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold tracking-wider flex-shrink-0"
            style={{ background: 'rgba(244,63,94,0.25)', color: '#FFE4E6' }}
          >
            MARKET_SELECTION_CONFLICT
          </span>
          <span>{data.cross_market_conflict.disagreement_reason || data.cross_market_conflict.warning}</span>
        </div>
      )}

      {expanded && (
        <div className="space-y-2 pt-2 border-t border-zinc-800">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-emerald-400 font-semibold mb-1">PROS</div>
              {(data.pros || []).length === 0 && (
                <div className="text-[10px] text-zinc-500">—</div>
              )}
              <ul data-testid="signal-quality-pros" className="space-y-0.5">
                {(data.pros || []).map((p, i) => (
                  <li key={i} className="text-emerald-200 text-[10px]">• {p}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] text-rose-400 font-semibold mb-1">RISKS</div>
              {(data.risks || []).length === 0 && (
                <div className="text-[10px] text-zinc-500">—</div>
              )}
              <ul data-testid="signal-quality-risks" className="space-y-0.5">
                {(data.risks || []).map((r, i) => (
                  <li key={i} className="text-rose-200 text-[10px]">⚠ {r}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-[10px] text-zinc-500 grid md:grid-cols-3 gap-2 pt-1">
            <div>
              <span className="text-zinc-500">prob (p_lo80):</span>{' '}
              <span className="text-zinc-200" data-testid="signal-quality-prob-cons">
                {fmtNum(data.probability_used, 4)}
              </span>
            </div>
            {data.probability_raw != null && (
              <div>
                <span className="text-zinc-500">prob (p_raw):</span>{' '}
                <span className="text-zinc-200" data-testid="signal-quality-prob-raw">
                  {fmtNum(data.probability_raw, 4)}
                </span>
              </div>
            )}
            <div>
              <span className="text-zinc-500">odds:</span>{' '}
              <span className="text-zinc-200">{fmtNum(data.odds, 2)}</span>
            </div>
            <div>
              <span className="text-zinc-500">kelly_full:</span>{' '}
              <span className="text-zinc-200">{fmtPct(data.kelly_full_pct)}</span>
            </div>
            {data.book_snapshot_at && (
              <div data-testid="signal-quality-book-snapshot">
                <span className="text-zinc-500">odds source:</span>{' '}
                <span className="text-zinc-200" title={String(data.book_snapshot_at)}>
                  {String(data.book_snapshot_at).slice(11, 19) || data.book_snapshot_at}
                </span>
              </div>
            )}
            {data.model_agreement_pct != null && (
              <div>
                <span className="text-zinc-500">agreement:</span>{' '}
                <span className="text-zinc-200">{fmtNum(data.model_agreement_pct, 0)}%</span>
              </div>
            )}
            {data.odds_age_min != null && (
              <div>
                <span className="text-zinc-500">odds age:</span>{' '}
                <span className="text-zinc-200">{data.odds_age_min}m</span>
              </div>
            )}
            {data.diagnostic_tags && data.diagnostic_tags.length > 0 && (
              <div className="md:col-span-3">
                <span className="text-zinc-500">tags:</span>{' '}
                {data.diagnostic_tags.map((t) => (
                  <span
                    key={t}
                    className="ml-1 px-1.5 py-0.5 rounded-sm bg-zinc-800/60 text-zinc-300 text-[9px]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* 2026-05-08 — Cross-Market Conflict detail block.
              Visible whenever the auditor evaluated the conditions
              (even if conflict=false) so the operator can see WHY
              there is or isn't a disagreement. */}
          {data.cross_market_conflict && !data.cross_market_conflict.error && (
            <div
              data-testid="cross-market-conflict-details"
              className="pt-2 border-t border-zinc-800 text-[10px]"
            >
              <div className="text-[10px] text-zinc-400 font-semibold mb-1 flex items-center gap-2">
                Cross-Market Selection Audit
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded-sm tracking-wider font-semibold ${
                    data.cross_market_conflict.conflict
                      ? 'bg-rose-700/40 text-rose-100'
                      : 'bg-emerald-700/30 text-emerald-200'
                  }`}
                  data-testid="cross-market-conflict-status"
                >
                  {data.cross_market_conflict.conflict ? 'CONFLICT' : 'no-conflict'}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-x-3 gap-y-0.5 text-zinc-400">
                <div>
                  <span className="text-zinc-500">paper_selected_market:</span>{' '}
                  <b className="text-zinc-200" data-testid="conflict-paper-market">
                    {data.cross_market_conflict.paper_selected_market || '—'}
                  </b>
                </div>
                <div>
                  <span className="text-zinc-500">shadow_recommended_market:</span>{' '}
                  <b className="text-amber-200" data-testid="conflict-shadow-market">
                    {data.cross_market_conflict.shadow_recommended_market || '—'}
                  </b>
                </div>
                <div>
                  <span className="text-zinc-500">cleaner_market_candidate:</span>{' '}
                  <b className="text-emerald-200" data-testid="conflict-cleaner-market">
                    {data.cross_market_conflict.cleaner_market_candidate || '—'}
                  </b>
                </div>
                <div>
                  <span className="text-zinc-500">model_agreement_pct:</span>{' '}
                  <span className={
                    data.cross_market_conflict.conditions?.agreement_le_threshold
                      ? 'text-rose-200' : 'text-zinc-300'
                  }>
                    {data.cross_market_conflict.conditions?.model_agreement_pct ?? '—'}%
                    {' '}<span className="text-zinc-600">
                      (≤{data.cross_market_conflict.conditions?.agreement_threshold_pct}%)
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">setscore_market_divergence_pp:</span>{' '}
                  <span className={
                    data.cross_market_conflict.conditions?.divergence_ge_threshold
                      ? 'text-rose-200' : 'text-zinc-300'
                  }>
                    {data.cross_market_conflict.conditions?.setscore_market_divergence_pp != null
                      ? `${Number(data.cross_market_conflict.conditions.setscore_market_divergence_pp).toFixed(1)}pp`
                      : '—'}
                    {' '}<span className="text-zinc-600">
                      (≥{data.cross_market_conflict.conditions?.divergence_threshold_pp}pp)
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">shadow_ev_cons_pct:</span>{' '}
                  <span className={
                    data.cross_market_conflict.conditions?.shadow_ev_ge_floor
                      ? 'text-emerald-200' : 'text-zinc-300'
                  }>
                    {data.cross_market_conflict.conditions?.shadow_ev_cons_pct != null
                      ? `+${Number(data.cross_market_conflict.conditions.shadow_ev_cons_pct).toFixed(1)}%`
                      : '—'}
                    {' '}<span className="text-zinc-600">
                      (≥+{data.cross_market_conflict.conditions?.shadow_ev_floor_pct}%)
                    </span>
                  </span>
                </div>
              </div>
              {data.cross_market_conflict.disagreement_reason && (
                <div className="mt-1 text-[9px] text-rose-200 leading-snug">
                  ↳ {data.cross_market_conflict.disagreement_reason}
                </div>
              )}
            </div>
          )}

          {candidates.filter((c) => c.ev_pct != null).length > 1 && (
            <div className="pt-2 border-t border-zinc-800">
              <div className="text-[10px] text-zinc-400 mb-1">All candidates:</div>
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left">market</th>
                    <th className="text-left">side</th>
                    <th className="text-right" title="Probability used for conservative EV (p_lo80 if available)">
                      prob (p_lo80)
                    </th>
                    <th className="text-right">odds</th>
                    <th className="text-right" title="Conservative EV: p_lo80 × odds − 1">EV cons</th>
                    <th className="text-right" title="Raw EV: p_calibrated × odds − 1">EV raw</th>
                    <th className="text-right">action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c, i) => (
                    <tr key={i} className="text-zinc-300">
                      <td>{c.market}</td>
                      <td>{c.side || '—'}</td>
                      <td className="text-right">{fmtNum(c.probability_used, 3)}</td>
                      <td className="text-right">{fmtNum(c.odds, 2)}</td>
                      <td
                        className={`text-right ${
                          c.ev_pct == null
                            ? 'text-zinc-500'
                            : c.ev_pct > 0
                            ? 'text-emerald-300'
                            : 'text-rose-300'
                        }`}
                      >
                        {fmtPct(c.ev_pct)}
                      </td>
                      <td
                        className={`text-right ${
                          c.ev_pct_raw == null
                            ? 'text-zinc-600'
                            : c.ev_pct_raw > 0
                            ? 'text-emerald-200/80'
                            : 'text-rose-200/80'
                        }`}
                      >
                        {c.ev_pct_raw == null ? '—' : fmtPct(c.ev_pct_raw)}
                      </td>
                      <td className="text-right">
                        {c.ev_pct != null && (
                          <button
                            type="button"
                            data-testid={`send-personal-${c.market}`}
                            onClick={() => handleSendClick(c)}
                            className="px-1.5 py-0.5 rounded-sm bg-violet-800/40 hover:bg-violet-700/50 text-violet-200 text-[9px]"
                          >
                            send
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="text-[9px] text-zinc-600 pt-1">
            scorer {data.scorer_version} · deterministic · no LLM
          </div>
        </div>
      )}
    </div>
  );
}
